import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import PersonPinIcon from "@mui/icons-material/PersonPin";
import SearchIcon from "@mui/icons-material/Search";
import UnmappedStores from "./UnmappedStores";

// ── Stable vendor identity key ─────────────────────────────────────────────────
// Vendors from the API sometimes have _id, sometimes id, sometimes both.
const vendorKey = (v) => v?._id ?? v?.id ?? null;

/** Haversine distance in km */
const haversineKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const fmtKm = (km) => (km == null || isNaN(km) ? "—" : `${km.toFixed(1)} KM`);

// Distinct soft colours for up to 6 vendor batches (cycles if more)
const VENDOR_COLOURS = [
  { bg: "#EEF4FF", border: "#1565C0", text: "#1565C0" },
  { bg: "#FFF3E0", border: "#E65100", text: "#E65100" },
  { bg: "#F3E5F5", border: "#6A1B9A", text: "#6A1B9A" },
  { bg: "#E8F5E9", border: "#2E7D32", text: "#2E7D32" },
  { bg: "#FCE4EC", border: "#AD1457", text: "#AD1457" },
  { bg: "#E0F7FA", border: "#00695C", text: "#00695C" },
];

const VendorsForSelectedDeliveryCenter = ({
  vendors = [],
  loading = false,
  selectedDC = null,
  mappedStores = [],
  distance = 0,
  onSaveBatches,
  isSaving = false,
  onAssignVendorToBatch,
  // unmapped stores tab
  unmappedStores = [],
  selectedUnmapped = [],
  setSelectedUnmapped,
  isDragging,
  dragPayload,
  handleDragStart,
  handleDragEnd,
  handleDropOnUnmapped,
  setDragOverDCId,
}) => {
  const [tab, setTab] = useState(0);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [storeSearch, setStoreSearch] = useState("");

  const dcLat = selectedDC?.coordinates?.latitude;
  const dcLng = selectedDC?.coordinates?.longitude;

  const dcStores = useMemo(() => {
    // return selectedDC?.stores || [];
    if (selectedDC?.vendorBatches?.length > 0) {
      return selectedDC.vendorBatches.flatMap((batch) => batch.stores || []);
    }
    return selectedDC?.stores || [];
  }, [selectedDC]);
  // ── Enrich vendors with distance from DC ─────────────────────────────────
  const vendorsWithDist = useMemo(
    () =>
      vendors.map((v) => {
        const vLat = parseFloat(
          v?.locationCoordinates?.latitude ?? v?.locationCoordinates?.lat,
        );
        const vLng = parseFloat(
          v?.locationCoordinates?.longitude ?? v?.locationCoordinates?.lng,
        );
        const distFromDC =
          dcLat && dcLng && !isNaN(vLat) && !isNaN(vLng)
            ? haversineKm(dcLat, dcLng, vLat, vLng)
            : null;
        return { ...v, lat: vLat, lng: vLng, distFromDC };
      }),
    [vendors, dcLat, dcLng],
  );

  // ── Build a flat lookup: store reference → { vendor, colourIdx } ──────────
  // This is the core of "visibility of which store is assigned to which vendor".
  // O(V × S) where V = vendors, S = stores — both are small numbers.
  const storeAssignmentMap = useMemo(() => {
    const map = new Map(); // store object ref → { vendor, colourIdx }
    (selectedDC?.vendorBatches || []).forEach((vb, idx) => {
      const colourIdx = idx % VENDOR_COLOURS.length;
      vb.stores.forEach((s) => {
        map.set(s, { vendor: vb.vendor, colourIdx });
      });
    });
    return map;
  }, [selectedDC?.vendorBatches]);

  // ── Per-store rows with nearest vendor ───────────────────────────────────
  const storeRows = useMemo(() => {
    return dcStores.map((store) => {
      const sLat = parseFloat(
        store.locationCoordinates?.lat ?? store.locationCoordinates?.latitude,
      );
      const sLng = parseFloat(
        store.locationCoordinates?.lng ?? store.locationCoordinates?.longitude,
      );
      const hasCoords = !isNaN(sLat) && !isNaN(sLng);

      let nearest = null;
      let nearestDist = Infinity;
      if (hasCoords) {
        vendorsWithDist.forEach((v) => {
          if (isNaN(v.lat) || isNaN(v.lng)) return;
          const d = haversineKm(sLat, sLng, v.lat, v.lng);
          if (d < nearestDist) {
            nearestDist = d;
            nearest = v;
          }
        });
      }
      return {
        store,
        sLat,
        sLng,
        nearest,
        nearestDist: nearestDist === Infinity ? null : nearestDist,
      };
    });
  }, [dcStores, vendorsWithDist]);

  const filteredRows = useMemo(() => {
    const q = storeSearch.toLowerCase();
    if (!q) return storeRows;
    return storeRows.filter(
      ({ store }) =>
        store.details?.storeName?.toLowerCase().includes(q) ||
        store.details?.city?.toLowerCase().includes(q),
    );
  }, [storeRows, storeSearch]);

  // ── Assign: move store exclusively to selectedVendor ─────────────────────
  // "Exclusively" means: remove from ANY other vendor batch first,
  // then add to the selected vendor's batch.
  const handleAssign = (store) => {
    if (!onAssignVendorToBatch || !selectedDC || !selectedVendor) return;

    const currentBatches = selectedDC.vendorBatches || [];
    const selKey = vendorKey(selectedVendor);

    // Build updated batches: strip this store from everyone, then add to target
    const updatedBatches = currentBatches.map((vb) => {
      const vbKey = vendorKey(vb.vendor);
      if (vbKey === selKey) {
        // Target vendor — add store if not already there
        const alreadyIn = vb.stores.includes(store);
        return alreadyIn ? vb : { ...vb, stores: [...vb.stores, store] };
      }
      // Other vendors — remove this store (reference equality)
      return { ...vb, stores: vb.stores.filter((s) => s !== store) };
    });

    // If selectedVendor has no batch yet, create one
    const alreadyHasBatch = currentBatches.some(
      (vb) => vendorKey(vb.vendor) === selKey,
    );
    if (!alreadyHasBatch) {
      updatedBatches.push({
        vendor: selectedVendor,
        stores: [store],
        batchTitle: `VB-${currentBatches.length + 1}`,
      });
    }

    // Push ALL batches at once so parent can do one setState
    onAssignVendorToBatch(selectedDC._id, updatedBatches);
  };

  // ── Unassign: remove store from whichever vendor currently has it ─────────
  const handleUnassign = (store) => {
    if (!onAssignVendorToBatch || !selectedDC) return;
    const currentBatches = selectedDC.vendorBatches || [];
    const updatedBatches = currentBatches.map((vb) => ({
      ...vb,
      stores: vb.stores.filter((s) => s !== store),
    }));
    onAssignVendorToBatch(selectedDC._id, updatedBatches);
  };

  // ─────────────────────────────────────────────────────────────────────────
  if (!selectedDC)
    return (
      <Box py={4} textAlign="center" color="text.secondary">
        <PersonPinIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
        <Typography variant="body2">
          Select a Delivery Center to view vendor partners.
        </Typography>
      </Box>
    );

  if (loading)
    return (
      <Box
        py={4}
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap={1}
      >
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading vendors for {selectedDC.cityName}…
        </Typography>
      </Box>
    );

  const vendorBatches = selectedDC?.vendorBatches || [];

  return (
    <Box>
      {/* ── Header: title + assignment summary chips + Save ─────────────────── */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
        gap={2}
      >
        <Box flex={1}>
          <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
            Available Vendor Partners
          </Typography>

          {/* Active vendor-batch summary chips */}
          {vendorBatches.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {vendorBatches.map((vb, idx) => {
                if (vb.stores.length === 0) return null; // hide empty batches
                const col = VENDOR_COLOURS[idx % VENDOR_COLOURS.length];
                return (
                  <Chip
                    key={idx}
                    size="small"
                    icon={
                      <StorefrontIcon sx={{ fontSize: 14, color: col.text }} />
                    }
                    label={`${vb.vendor?.vendorBusinessName || "Vendor"} — ${vb.stores.length} store${vb.stores.length > 1 ? "s" : ""}`}
                    onDelete={() => {
                      // Clear all stores from this vendor batch
                      const updated = vendorBatches.map((b, i) =>
                        i === idx ? { ...b, stores: [] } : b,
                      );
                      onAssignVendorToBatch(selectedDC._id, updated);
                    }}
                    sx={{
                      bgcolor: col.bg,
                      border: `1px solid ${col.border}`,
                      color: col.text,
                      fontWeight: 600,
                      "& .MuiChip-deleteIcon": {
                        color: col.text,
                        opacity: 0.6,
                      },
                    }}
                  />
                );
              })}
            </Stack>
          )}
        </Box>

        <Button
          size="small"
          variant="contained"
          color="success"
          startIcon={
            isSaving ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          disabled={isSaving}
          onClick={onSaveBatches}
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          {isSaving ? "Saving…" : "Save Batch"}
        </Button>
      </Box>

      {/* ── Vendor cards ────────────────────────────────────────────────────── */}
      {vendors.length === 0 ? (
        <Typography variant="body2" color="text.secondary" mb={2}>
          No approved vendors found for {selectedDC.cityName}.
        </Typography>
      ) : (
        <div>
          <div className="relative w-1/2 mb-2">
            <select
              value={vendorKey(selectedVendor) ?? ""}
              onChange={(e) => {
                const key = e.target.value;
                const vendor =
                  vendorsWithDist.find((v) => vendorKey(v) === key) || null;
                setSelectedVendor(vendor);
              }}
              className="h-9 w-full appearance-none rounded-md border border-gray-300 bg-white px-3 pr-8 text-[12px] text-gray-700 outline-none focus:border-[#0798f0]"
            >
              <option value="">Select Vendor</option>

              {[...vendorsWithDist]
                .sort((a, b) => {
                  if (a.distFromDC == null && b.distFromDC == null) return 0;
                  if (a.distFromDC == null) return 1;
                  if (b.distFromDC == null) return -1;
                  return a.distFromDC - b.distFromDC;
                })
                .map((vendor, idx) => {
                  const key = vendorKey(vendor);

                  const vbIdx = vendorBatches.findIndex(
                    (vb) => vendorKey(vb.vendor) === key,
                  );
                  const assignedCount =
                    vbIdx >= 0 ? vendorBatches[vbIdx].stores.length : 0;

                  const label = [
                    vendor.vendorBusinessName || vendor.name,
                    vendor.distFromDC != null
                      ? `${fmtKm(vendor.distFromDC)} from DC`
                      : null,
                    assignedCount > 0
                      ? `${assignedCount} store${assignedCount > 1 ? "s" : ""}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" — ");

                  return (
                    <option key={key ?? idx} value={key ?? ""}>
                      {label}
                    </option>
                  );
                })}
            </select>

            <KeyboardArrowDown
              className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2"
              sx={{
                fontSize: 17,
              }}
            />
          </div>
        </div>
      )}

      {/* Instruction line */}
      {selectedVendor ? (
        <Typography
          variant="caption"
          color="primary"
          fontWeight={600}
          display="block"
          mb={1}
        >
          ✓ {selectedVendor.vendorBusinessName} selected — click Assign on any
          store row below. Assigning a store moves it exclusively to this
          vendor.
        </Typography>
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mb={1}
        >
          Click a vendor card above to select it, then use Assign on store rows.
        </Typography>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* ── Tabs + search ────────────────────────────────────────────────────── */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ "& .MuiTab-root": { fontSize: 12, minWidth: 80 } }}
        >
          <Tab label={`Unmapped Stores (${unmappedStores.length})`} />
          <Tab label={`DC Stores (${dcStores.length})`} />
        </Tabs>

        <TextField
          size="small"
          placeholder="Search store…"
          value={storeSearch}
          onChange={(e) => setStoreSearch(e.target.value)}
          sx={{ width: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* ── Tab 0: Unmapped Stores ───────────────────────────────────────────── */}
      {tab === 0 ? (
        <UnmappedStores
          stores={unmappedStores}
          selectedStores={selectedUnmapped}
          onSelectionChange={setSelectedUnmapped}
          isDragging={isDragging}
          dragPayload={dragPayload}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDropOnUnmapped={handleDropOnUnmapped}
          onDragOverUnmapped={(e) => {
            e.preventDefault();
            setDragOverDCId(null);
          }}
          // Shared search box above the tabs now drives this tab's filtering too
          search={storeSearch}
        />
      ) : (
        /* ── Tab 1: DC Stores with vendor assignment ─────────────────────────── */
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderRadius: 2, maxHeight: 400, overflowY: "auto" }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow
                sx={{
                  "& th": { bgcolor: "#F8F9FA", fontWeight: 700, fontSize: 11 },
                }}
              >
                <TableCell>STORE NAME</TableCell>
                <TableCell>STORE CITY</TableCell>
                <TableCell>DELIVERY CENTER</TableCell>
                <TableCell>NEAREST VENDOR</TableCell>
                <TableCell align="center">
                  DISTANCE FROM SELECTED VENDOR
                </TableCell>
                <TableCell align="center">ASSIGNED TO</TableCell>
                <TableCell align="center">ACTION</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    align="center"
                    sx={{ py: 4, color: "text.secondary" }}
                  >
                    {dcStores.length === 0
                      ? "No stores assigned to this DC yet."
                      : "No stores match your search."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map(
                  ({ store, sLat, sLng, nearest, nearestDist }, i) => {
                    // Who is this store currently assigned to?
                    const assignment = storeAssignmentMap.get(store); // { vendor, colourIdx } | undefined
                    const isAssigned = Boolean(assignment);
                    const assignedCol = isAssigned
                      ? VENDOR_COLOURS[assignment.colourIdx]
                      : null;

                    // Distance to the currently selected vendor card
                    const distToSelected =
                      selectedVendor &&
                      !isNaN(sLat) &&
                      !isNaN(sLng) &&
                      !isNaN(selectedVendor.lat) &&
                      !isNaN(selectedVendor.lng)
                        ? haversineKm(
                            sLat,
                            sLng,
                            selectedVendor.lat,
                            selectedVendor.lng,
                          )
                        : null;

                    // Is this store already in the selected vendor's batch?
                    const alreadyInSelectedVendor =
                      isAssigned &&
                      vendorKey(assignment.vendor) ===
                        vendorKey(selectedVendor);

                    return (
                      <TableRow
                        hover
                        key={i}
                        sx={{
                          // Tint the row with the assigned vendor's colour
                          bgcolor: isAssigned ? assignedCol.bg : undefined,
                          "&:hover": {
                            bgcolor: isAssigned ? assignedCol.bg : undefined,
                          },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {store.details?.storeName || "—"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {store.details?.address}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {store.details?.city || "—"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {selectedDC.cityName}
                            {selectedDC.stateName
                              ? ` (${selectedDC.stateName})`
                              : ""}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          {nearest ? (
                            <Typography variant="body2">
                              {nearest.vendorBusinessName || nearest.name}
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                ml={0.5}
                              >
                                ({fmtKm(nearestDist)})
                              </Typography>
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              No vendor nearby
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={fmtKm(distToSelected)}
                            variant="outlined"
                            color={
                              distToSelected != null &&
                              distToSelected < distance
                                ? "success"
                                : "default"
                            }
                          />
                        </TableCell>

                        {/* ASSIGNED TO column — the key visibility column */}
                        <TableCell align="center">
                          {isAssigned ? (
                            <Chip
                              size="small"
                              label={
                                assignment.vendor?.vendorBusinessName ||
                                "Vendor"
                              }
                              sx={{
                                bgcolor: assignedCol.bg,
                                border: `1px solid ${assignedCol.border}`,
                                color: assignedCol.text,
                                fontWeight: 600,
                                fontSize: 10,
                              }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>

                        {/* ACTION column */}
                        <TableCell align="center">
                          {isAssigned ? (
                            // Show Unassign if already assigned (to any vendor)
                            <Tooltip
                              title={`Remove from ${assignment.vendor?.vendorBusinessName}`}
                            >
                              <Button
                                size="small"
                                variant="text"
                                color="error"
                                startIcon={
                                  <DoNotDisturbOnIcon sx={{ fontSize: 14 }} />
                                }
                                sx={{
                                  fontWeight: 600,
                                  fontSize: 11,
                                  whiteSpace: "nowrap",
                                }}
                                onClick={() => handleUnassign(store)}
                              >
                                Unassign
                              </Button>
                            </Tooltip>
                          ) : selectedVendor ? (
                            // Not assigned + vendor selected → show Assign
                            <Tooltip
                              title={`Assign to ${selectedVendor.vendorBusinessName}`}
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={
                                  <CheckCircleIcon sx={{ fontSize: 14 }} />
                                }
                                sx={{
                                  fontWeight: 600,
                                  fontSize: 11,
                                  whiteSpace: "nowrap",
                                }}
                                onClick={() => handleAssign(store)}
                              >
                                Assign
                              </Button>
                            </Tooltip>
                          ) : (
                            // Not assigned + no vendor selected
                            <Typography variant="caption" color="text.disabled">
                              Select vendor ↑
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  },
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default VendorsForSelectedDeliveryCenter;
